/**
 * Beregninger for Pipe-brettet (CRM-2.5). Ren kalkylefil: grupperer åpne
 * prosesser i Outreach, Møte og Tilbud, flagger stillstand og regner vektet
 * verdi over Tilbud-kolonnen med samme formel som salgsdashboardet.
 */
import type { DBOpportunity } from "./db-types";
import { daysInStage, isStalled, type StallThresholds } from "./opportunity-calc";
import {
  weightedPipeline,
  type PipelineOffer,
  type ProbabilityWeights,
  type WeightedPipelineResult,
} from "./offer-pipeline-calc";

export type PipeStage = "outreach" | "meeting" | "offer";
export const PIPE_STAGES: readonly PipeStage[] = ["outreach", "meeting", "offer"];

export interface PipeCardData<O extends DBOpportunity, F extends PipelineOffer> {
  opportunity: O;
  offer: F | null;
  days: number;
  stalled: boolean;
}

export interface PipeBoard<O extends DBOpportunity, F extends PipelineOffer> {
  columns: Record<PipeStage, PipeCardData<O, F>[]>;
  /** Vektet verdi over Tilbud-kolonnen. */
  weighted: WeightedPipelineResult;
}

interface BuildInput<O extends DBOpportunity, F extends PipelineOffer> {
  opportunities: readonly O[];
  offers: readonly F[];
  weights: ProbabilityWeights;
  thresholds: StallThresholds;
  now: Date;
}

function isPipeStage(stage: DBOpportunity["stage"]): stage is PipeStage {
  return (PIPE_STAGES as readonly string[]).includes(stage);
}

/** Eldste først i hver kolonne, så det som har stått lengst står øverst. */
export function buildPipeBoard<O extends DBOpportunity, F extends PipelineOffer>({
  opportunities,
  offers,
  weights,
  thresholds,
  now,
}: BuildInput<O, F>): PipeBoard<O, F> {
  const offerById = new Map(offers.map((o) => [o.id, o]));
  const columns: PipeBoard<O, F>["columns"] = { outreach: [], meeting: [], offer: [] };

  for (const opportunity of opportunities) {
    if (!isPipeStage(opportunity.stage)) continue;
    const offer = opportunity.offer_id ? (offerById.get(opportunity.offer_id) ?? null) : null;
    columns[opportunity.stage].push({
      opportunity,
      offer,
      days: daysInStage(opportunity.stage_entered_at, now),
      stalled: isStalled(opportunity, offer, thresholds, now),
    });
  }
  for (const stage of PIPE_STAGES) {
    columns[stage].sort((a, b) => a.opportunity.stage_entered_at.localeCompare(b.opportunity.stage_entered_at));
  }

  const offerColumnOffers = columns.offer.flatMap((c) => (c.offer ? [c.offer] : []));
  return { columns, weighted: weightedPipeline(offerColumnOffers, weights, now) };
}
